import React from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HistoryIcon from '@mui/icons-material/History'
import ScheduleIcon from '@mui/icons-material/Schedule'
import { ISystemUptimeEvent, ISystemVersioningEvent } from '../graphql'
import { statusColors } from '../theme'
import dayjs from 'dayjs'

interface EventTimelineProps {
  uptimeHistory: ISystemUptimeEvent[]
  uptimePlanned: ISystemUptimeEvent[]
  versioningHistory: ISystemVersioningEvent[]
}

const UptimeEventItem: React.FC<{ event: ISystemUptimeEvent }> = ({ event }) => {
  const statusColor = statusColors[event.status]

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: statusColor,
          mt: 0.5,
          flexShrink: 0,
        }}
      />
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
          <Chip
            label={event.status}
            size="small"
            sx={{
              backgroundColor: statusColor,
              color: 'white',
              fontWeight: 600,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {dayjs(event.startDatetime).format('MMM D, YYYY h:mm A')}
          </Typography>
        </Box>
        <Typography variant="body2">{event.description}</Typography>
      </Box>
    </Box>
  )
}

const VersionEventItem: React.FC<{ event: ISystemVersioningEvent }> = ({ event }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: 'primary.main',
          mt: 0.5,
          flexShrink: 0,
        }}
      />
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
          <Chip label={`v${event.versionNumber}`} size="small" color="primary" />
          <Typography
            variant="caption"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: 'action.hover',
              px: 0.5,
              borderRadius: 0.5,
            }}
          >
            {event.versionHash.substring(0, 8)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dayjs(event.startDatetime).format('MMM D, YYYY h:mm A')}
          </Typography>
        </Box>
        <Typography variant="body2">{event.description}</Typography>
      </Box>
    </Box>
  )
}

export const EventTimeline: React.FC<EventTimelineProps> = ({
  uptimeHistory,
  uptimePlanned,
  versioningHistory,
}) => {
  const hasPlannedEvents = uptimePlanned.length > 0
  const hasUptimeHistory = uptimeHistory.length > 0
  const hasVersionHistory = versioningHistory.length > 0

  // Helper function to render events in reverse order
  // Uses original array indices as React keys to ensure stable component identity
  const renderReversed = <T,>(
    items: T[],
    renderFn: (item: T, key: string) => React.ReactElement
  ) => {
    const result: React.ReactElement[] = []
    for (let i = items.length - 1; i >= 0; i--) {
      // Key is the original index, providing stable identity even when display order changes
      result.push(renderFn(items[i], String(i)))
    }
    return result
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Planned Maintenance */}
      {hasPlannedEvents && (
        <Card sx={{ backgroundColor: 'background.paper' }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon color="info" />
                <Typography variant="h6">Scheduled Maintenance</Typography>
                <Chip label={uptimePlanned.length} size="small" color="info" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {renderReversed(uptimePlanned, (event, key) => (
                <UptimeEventItem key={key} event={event} />
              ))}
            </AccordionDetails>
          </Accordion>
        </Card>
      )}

      {/* Uptime History */}
      {hasUptimeHistory && (
        <Card sx={{ backgroundColor: 'background.paper' }}>
          <Accordion defaultExpanded={!hasPlannedEvents}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon color="primary" />
                <Typography variant="h6">Uptime History</Typography>
                <Chip label={uptimeHistory.length} size="small" color="primary" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {renderReversed(uptimeHistory, (event, key) => (
                <UptimeEventItem key={key} event={event} />
              ))}
            </AccordionDetails>
          </Accordion>
        </Card>
      )}

      {/* Version History */}
      {hasVersionHistory && (
        <Card sx={{ backgroundColor: 'background.paper' }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon color="secondary" />
                <Typography variant="h6">Version History</Typography>
                <Chip label={versioningHistory.length} size="small" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {renderReversed(versioningHistory, (event, key) => (
                <VersionEventItem key={key} event={event} />
              ))}
            </AccordionDetails>
          </Accordion>
        </Card>
      )}

      {!hasPlannedEvents && !hasUptimeHistory && !hasVersionHistory && (
        <Card sx={{ backgroundColor: 'background.paper' }}>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              No historical events to display
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}
